import { AppDispatch } from "../../../app/store";
import { CreateMemberRequest } from "../contracts/CreateMemberRequest";
import { UpdateMemberRequest } from "../contracts/UpdateMemberRequest";
import {
  addMemberToGroup,
  assignMemberInstructor,
  createMember,
  removeMemberFromGroup,
  updateMember,
} from "../slice-extensions/members-slice-extension";
import { Group } from "../models/Group";
import { Member } from "../models/Member";

export interface MemberFormValues {
  firstName: string;
  lastName: string;
  email: string;
  /** Base ISO language code (en/de/uk/pl/ja/ar) — the language of the member's emails. */
  primaryLanguageCode: string;
  address: string;
  notes: string;
  instructorId: string | null;
  selectedGroups: Group[];
}

export type SaveMemberResult =
  | { ok: true; memberId: string; assignFailed: boolean }
  | { ok: false };

const toDeviceTimeZone = () => ({
  timeZoneName: Intl.DateTimeFormat().resolvedOptions().timeZone,
  timeZoneUtcOffsetHours: new Date().getTimezoneOffset() / -60,
});

/**
 * Persists the member form: create or update, then reconcile the assigned
 * instructor (dedicated endpoint — the update contract doesn't carry it) and
 * the group enrollment diff. Pure orchestration; the caller owns UI feedback.
 */
export const saveMember = async (
  dispatch: AppDispatch,
  values: MemberFormValues,
  existing: Member | null,
): Promise<SaveMemberResult> => {
  const language = values.primaryLanguageCode.trim();
  const common = {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    ...toDeviceTimeZone(),
    email: values.email.trim() || null,
    notes: values.notes.trim() || null,
    address: values.address.trim() || null,
  };

  if (existing) {
    const request: UpdateMemberRequest = {
      ...common,
      primaryLanguageCode: language || existing.primaryLanguageCode,
      dateOfBirth: existing.dateOfBirth,
      guardianMemberId: existing.guardianMemberId,
    };
    const result = await dispatch(updateMember({ id: existing.id, request }));
    if (!updateMember.fulfilled.match(result)) {
      return { ok: false };
    }

    const assignFailed = !(await reconcileInstructor(dispatch, existing.id,
      existing.instructorId, values.instructorId));

    const currentIds = new Set((existing.groups || []).map((g) => g.id));
    const selectedIds = new Set(values.selectedGroups.map((g) => g.id));
    for (const group of values.selectedGroups.filter((g) => !currentIds.has(g.id))) {
      await dispatch(addMemberToGroup({ groupId: group.id, memberId: existing.id }));
    }
    for (const group of (existing.groups || []).filter((g) => !selectedIds.has(g.id))) {
      await dispatch(removeMemberFromGroup({ groupId: group.id, memberId: existing.id }));
    }

    return { ok: true, memberId: existing.id, assignFailed };
  }

  const request: CreateMemberRequest = {
    ...common,
    // Blank = no explicit choice: the API stores the organization's language.
    primaryLanguageCode: language || null,
    instructorId: values.instructorId,
    dateOfBirth: null,
    guardianMemberId: null,
    tags: [],
  };
  const result = await dispatch(createMember(request));
  if (!createMember.fulfilled.match(result)) {
    return { ok: false };
  }

  const created = result.payload;
  const assignFailed = !(await reconcileInstructor(dispatch, created.id,
    created.instructorId, values.instructorId));

  for (const group of values.selectedGroups) {
    await dispatch(addMemberToGroup({ groupId: group.id, memberId: created.id }));
  }

  return { ok: true, memberId: created.id, assignFailed };
};

/** Reassigns when the picked instructor differs; true when nothing failed. */
const reconcileInstructor = async (
  dispatch: AppDispatch,
  memberId: string,
  currentInstructorId: string | null,
  pickedInstructorId: string | null,
): Promise<boolean> => {
  if (!pickedInstructorId || pickedInstructorId === currentInstructorId) {
    return true;
  }
  const result = await dispatch(
    assignMemberInstructor({ id: memberId, request: { instructorId: pickedInstructorId } }),
  );
  return assignMemberInstructor.fulfilled.match(result);
};
